/**
 * SSO/SCIM Store
 * Zustand store for managing SSO and SCIM state
 */

import { create } from 'zustand';
import type {
  SSOConfig,
  SSOConfigInput,
  SCIMConfig,
  SCIMConfigInput,
  SCIMGroup,
  DomainVerification,
} from './types';
import * as api from './api';

interface SSOState {
  // SSO Configuration
  ssoConfig: SSOConfig | null;
  ssoLoading: boolean;
  ssoError: string | null;

  // SCIM Configuration
  scimConfig: SCIMConfig | null;
  scimLoading: boolean;
  scimError: string | null;
  newScimToken: string | null;

  // SCIM Groups
  scimGroups: SCIMGroup[];
  groupsLoading: boolean;

  // Domain Verification
  domainVerification: DomainVerification | null;
  verificationLoading: boolean;

  // Actions
  loadSSOConfig: () => Promise<void>;
  saveSSOConfig: (config: SSOConfigInput) => Promise<void>;
  deleteSSOConfig: () => Promise<void>;
  toggleSSO: (enabled: boolean) => Promise<void>;
  enforceSSO: (enforced: boolean) => Promise<void>;
  getDomainVerification: (domain: string) => Promise<DomainVerification>;
  verifyDomain: (domain: string) => Promise<boolean>;
  testSSOLogin: () => Promise<string>;

  loadSCIMConfig: () => Promise<void>;
  updateSCIMConfig: (config: SCIMConfigInput) => Promise<void>;
  generateSCIMToken: () => Promise<string>;
  rotateSCIMToken: () => Promise<string>;
  triggerSync: () => Promise<void>;
  clearNewToken: () => void;

  loadSCIMGroups: () => Promise<void>;
  updateGroupMapping: (groupId: string, role: string, permissions?: string[]) => Promise<void>;

  clearErrors: () => void;
}

export const useSSOStore = create<SSOState>((set, get) => ({
  // Initial state
  ssoConfig: null,
  ssoLoading: false,
  ssoError: null,

  scimConfig: null,
  scimLoading: false,
  scimError: null,
  newScimToken: null,

  scimGroups: [],
  groupsLoading: false,

  domainVerification: null,
  verificationLoading: false,

  // SSO Actions
  loadSSOConfig: async () => {
    set({ ssoLoading: true, ssoError: null });
    try {
      const response = await api.getSSOConfig();
      set({ ssoConfig: response.data, ssoLoading: false });
    } catch (error) {
      set({
        ssoError: (error as Error).message,
        ssoLoading: false,
      });
    }
  },

  saveSSOConfig: async (config: SSOConfigInput) => {
    set({ ssoLoading: true, ssoError: null });
    try {
      const response = await api.saveSSOConfig(config);
      set({ ssoConfig: response.data, ssoLoading: false });
    } catch (error) {
      set({
        ssoError: (error as Error).message,
        ssoLoading: false,
      });
      throw error;
    }
  },

  deleteSSOConfig: async () => {
    set({ ssoLoading: true, ssoError: null });
    try {
      await api.deleteSSOConfig();
      set({ ssoConfig: null, ssoLoading: false });
    } catch (error) {
      set({
        ssoError: (error as Error).message,
        ssoLoading: false,
      });
      throw error;
    }
  },

  toggleSSO: async (enabled: boolean) => {
    set({ ssoLoading: true, ssoError: null });
    try {
      const response = await api.toggleSSO(enabled);
      set({ ssoConfig: response.data, ssoLoading: false });
    } catch (error) {
      set({
        ssoError: (error as Error).message,
        ssoLoading: false,
      });
      throw error;
    }
  },

  enforceSSO: async (enforced: boolean) => {
    set({ ssoLoading: true, ssoError: null });
    try {
      const response = await api.enforceSSSO(enforced);
      set({ ssoConfig: response.data, ssoLoading: false });
    } catch (error) {
      set({
        ssoError: (error as Error).message,
        ssoLoading: false,
      });
      throw error;
    }
  },

  getDomainVerification: async (domain: string) => {
    set({ verificationLoading: true });
    try {
      const response = await api.getDomainVerification(domain);
      set({ domainVerification: response.data, verificationLoading: false });
      return response.data;
    } catch (error) {
      set({ verificationLoading: false });
      throw error;
    }
  },

  verifyDomain: async (domain: string) => {
    set({ verificationLoading: true });
    try {
      const response = await api.verifyDomain(domain);
      set({ domainVerification: response.data, verificationLoading: false });
      // Reload SSO config to get updated domain verification status
      get().loadSSOConfig();
      return response.data.verified;
    } catch (error) {
      set({ verificationLoading: false });
      throw error;
    }
  },

  testSSOLogin: async () => {
    const response = await api.testSSOLogin();
    return response.data.redirect_url;
  },

  // SCIM Actions
  loadSCIMConfig: async () => {
    set({ scimLoading: true, scimError: null });
    try {
      const response = await api.getSCIMConfig();
      set({ scimConfig: response.data, scimLoading: false });
    } catch (error) {
      set({
        scimError: (error as Error).message,
        scimLoading: false,
      });
    }
  },

  updateSCIMConfig: async (config: SCIMConfigInput) => {
    set({ scimLoading: true, scimError: null });
    try {
      const response = await api.updateSCIMConfig(config);
      set({ scimConfig: response.data, scimLoading: false });
    } catch (error) {
      set({
        scimError: (error as Error).message,
        scimLoading: false,
      });
      throw error;
    }
  },

  generateSCIMToken: async () => {
    set({ scimLoading: true, scimError: null });
    try {
      const response = await api.generateSCIMToken();
      set({ newScimToken: response.data.token, scimLoading: false });
      // Reload config to get new prefix
      get().loadSCIMConfig();
      return response.data.token;
    } catch (error) {
      set({
        scimError: (error as Error).message,
        scimLoading: false,
      });
      throw error;
    }
  },

  rotateSCIMToken: async () => {
    set({ scimLoading: true, scimError: null });
    try {
      const response = await api.rotateSCIMToken();
      set({ newScimToken: response.data.token, scimLoading: false });
      // Reload config to get new prefix
      get().loadSCIMConfig();
      return response.data.token;
    } catch (error) {
      set({
        scimError: (error as Error).message,
        scimLoading: false,
      });
      throw error;
    }
  },

  triggerSync: async () => {
    set({ scimLoading: true, scimError: null });
    try {
      await api.triggerSCIMSync();
      // Reload config to get updated sync status
      await get().loadSCIMConfig();
      set({ scimLoading: false });
    } catch (error) {
      set({
        scimError: (error as Error).message,
        scimLoading: false,
      });
      throw error;
    }
  },

  clearNewToken: () => {
    set({ newScimToken: null });
  },

  // SCIM Groups
  loadSCIMGroups: async () => {
    set({ groupsLoading: true });
    try {
      const response = await api.listSCIMGroups();
      set({ scimGroups: response.data, groupsLoading: false });
    } catch (error) {
      set({ groupsLoading: false });
      throw error;
    }
  },

  updateGroupMapping: async (groupId: string, role: string, permissions?: string[]) => {
    try {
      await api.updateGroupMapping(groupId, { role, permissions });
      // Reload groups
      get().loadSCIMGroups();
    } catch (error) {
      throw error;
    }
  },

  clearErrors: () => {
    set({ ssoError: null, scimError: null });
  },
}));

// Selectors
export const useSSOConfig = () => useSSOStore((state) => state.ssoConfig);
export const useSCIMConfig = () => useSSOStore((state) => state.scimConfig);
export const useSCIMGroups = () => useSSOStore((state) => state.scimGroups);
export const useSSOLoading = () => useSSOStore((state) => state.ssoLoading || state.scimLoading);
export const useSSOError = () => useSSOStore((state) => state.ssoError || state.scimError);
